/**
 * Módulo de tracking de navegação — buffer local + normalizador do
 * dataLayer. Ver docs/superpowers/specs/2026-08-12-cookie-navegacao-eventos-design.md
 * pro desenho completo; ver o comentário de `normalizeDataLayerEvent` abaixo
 * pro porquê da interceptação ser em `window.dataLayer.push` e não nos
 * call-sites GA4 (3 mecanismos de disparo distintos, nenhum centralizado).
 */

export const BUFFER_STORAGE_KEY = "dt_nav_events_buffer";

export const VALID_EVENT_TYPES = ["view_category", "view_product", "add_to_cart"] as const;
export type NavEventType = (typeof VALID_EVENT_TYPES)[number];

export interface NavEvent {
  tipo: NavEventType;
  categoria?: string;
  produto_sku?: string;
  timestamp: string;
}

// Nome do evento GA4 (como já disparado pelos call-sites existentes) -> tipo
// que o endpoint /api/tracking/eventos-navegacao do CRM aceita.
// `begin_checkout` é DELIBERADAMENTE omitido: o CRM rejeita o lote inteiro
// se qualquer evento tiver `tipo` fora de view_category/view_product/
// add_to_cart (ver Global Constraints do plano) — begin_checkout vai só
// pelo proxy carrinho-iniciado (src/server/tracking.ts).
const GA4_TO_CRM_EVENT: Record<string, NavEventType> = {
  view_item_list: "view_category",
  view_item: "view_product",
  add_to_cart: "add_to_cart",
};

interface AnalyticsItemLike {
  item_id?: string;
  item_category?: string;
}

interface DataLayerPushArg {
  event?: string;
  // Formato usado por sendEvent()/SendEventOnLoad: items dentro de `ecommerce`.
  ecommerce?: {
    items?: AnalyticsItemLike[];
    item_list_name?: string;
  } | null;
  // Formato usado por useSendEvent()+ANALYTICS_SCRIPT (@decocms/start): tudo
  // spread direto no objeto do evento, sem wrapper `ecommerce`.
  items?: AnalyticsItemLike[];
  item_list_name?: string;
}

/**
 * Reconhece os 2 formatos de payload que hoje chegam em
 * `window.dataLayer.push(...)` neste repo (ver tabela no cabeçalho da Task 2
 * do plano) e devolve um `NavEvent` pronto pro buffer, ou `null` se o push
 * não for um dos 3 eventos que o CRM aceita nesse endpoint (inclui o push
 * de reset `{ecommerce: null}` que `sendEvent()`/`SendEventOnLoad` sempre
 * fazem antes do push real, e `begin_checkout`, que tem seu próprio caminho).
 */
export function normalizeDataLayerEvent(arg: unknown): NavEvent | null {
  if (!arg || typeof arg !== "object") return null;
  const push = arg as DataLayerPushArg;
  if (!push.event) return null;

  const tipo = GA4_TO_CRM_EVENT[push.event];
  if (!tipo) return null;

  const timestamp = new Date().toISOString();
  // `ecommerce` presente (mesmo que `{}`) indica o formato wrapped; senão,
  // os campos estão soltos no próprio objeto do push (formato flat).
  const container = push.ecommerce ?? push;

  if (tipo === "view_category") {
    return {
      tipo,
      categoria: container.item_list_name || undefined,
      timestamp,
    };
  }

  const firstItem = container.items?.[0];
  return {
    tipo,
    categoria: firstItem?.item_category || undefined,
    produto_sku: firstItem?.item_id || undefined,
    timestamp,
  };
}

// ---------------------------------------------------------------------------
// Buffer (localStorage) — ver Task 3 pra quem chama isso e Task 5 pra quem
// consome o resultado.
// ---------------------------------------------------------------------------

/**
 * Todo acesso ao `localStorage` daqui pra baixo está DENTRO de try/catch,
 * incluindo o próprio `typeof localStorage` (Finding 2 da revisão final):
 * em contextos reais o getter `localStorage` LANÇA (`SecurityError` em
 * Safari privado historicamente, escudos do Brave, política corporativa
 * bloqueando dados de site), e `setItem` lança `QuotaExceededError` com a
 * cota cheia. Um guard de `undefined` não cobre nenhum dos dois. Como essas
 * funções rodam dentro do wrapper de `dataLayer.push`, um throw aqui subiria
 * pelo `sendEvent()` do call-site e quebraria UI real -- tracking nunca pode
 * quebrar a loja.
 */
export function readBuffer(): NavEvent[] {
  try {
    if (typeof localStorage === "undefined") return [];
    const raw = localStorage.getItem(BUFFER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as NavEvent[]) : [];
  } catch {
    return [];
  }
}

export function writeBuffer(events: NavEvent[]): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(BUFFER_STORAGE_KEY, JSON.stringify(events));
  } catch {
    // cota estourada / storage bloqueado -- perder eventos de tracking é
    // aceitável, propagar a exceção não é.
  }
}

export function pushBufferEvent(event: NavEvent): void {
  const events = readBuffer();
  events.push(event);
  writeBuffer(events);
}

export function clearBuffer(): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(BUFFER_STORAGE_KEY);
  } catch {
    // idem writeBuffer
  }
}

import { hasCookieConsent } from "~/utils/consent";
import { getOrCreateVisitorId } from "~/utils/visitorId";
import { forwardFacebookCapiEvents } from "~/sdk/metaCapi";

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

/**
 * Marca posta no NOSSO wrapper. É o que permite detectar que alguém
 * (tipicamente o GTM, que carrega DEPOIS de nós -- ver o carregamento
 * adiado em src/routes/__root.tsx) substituiu `window.dataLayer.push` e
 * descartou a nossa interceptação. Ver Finding 3 da revisão final.
 */
export const NAV_TRACKING_HOOK_MARKER = "__dtNavTrackingInstalled";

/**
 * Só o wrapper ATUAL processa eventos. Se algum terceiro tiver encadeado o
 * push dele por cima do nosso (em vez de substituir), a re-asserção abaixo
 * cria um wrapper novo por fora -- e sem esta checagem o wrapper antigo,
 * ainda vivo lá dentro da cadeia, bufferizaria cada evento uma SEGUNDA vez.
 */
let activeWrapper: ((...args: unknown[]) => number) | null = null;

function wrapDataLayerPush(): void {
  window.dataLayer = window.dataLayer || [];
  // Eventos Meta que entraram na fila sem passar pelo wrapper (antes dele
  // existir, ou com o GTM por cima) -- ver src/sdk/metaCapi.ts.
  forwardFacebookCapiEvents(window.dataLayer);
  const original = window.dataLayer.push.bind(window.dataLayer);

  const wrapped = ((...args: unknown[]) => {
    if (wrapped === activeWrapper) {
      // Todo o trabalho de tracking é best-effort: qualquer exceção aqui
      // (localStorage lançando, JSON gigante, o que for) morre neste catch.
      // Sem isto, um throw subiria pelo `sendEvent()` do call-site -- em
      // AddToCartButton/common.tsx o `sendEvent` está num `try/finally` SEM
      // catch, e a linha que ABRE o mini-cart (`displayCart.value = true`)
      // vem depois dele: o item entraria no carrinho e a gaveta nunca
      // abriria. Ver Finding 2 da revisão final.
      forwardFacebookCapiEvents(args); // nunca lança (try/catch interno)
      try {
        if (hasCookieConsent()) {
          for (const arg of args) {
            const navEvent = normalizeDataLayerEvent(arg);
            if (navEvent) pushBufferEvent(navEvent);
          }
        }
      } catch (error) {
        console.error("[navTracking] falha ao capturar push do dataLayer:", error);
      }
    }
    // SEMPRE repassa, tenha o tracking funcionado ou não.
    return original(...args);
  }) as ((...args: unknown[]) => number) & Record<string, unknown>;

  wrapped[NAV_TRACKING_HOOK_MARKER] = true;
  activeWrapper = wrapped;
  window.dataLayer.push = wrapped as unknown as typeof window.dataLayer.push;
}

/**
 * Garante que o NOSSO wrapper está instalado em `window.dataLayer.push`,
 * re-instalando por cima do que estiver lá se a marca tiver sumido.
 *
 * Substitui o antigo guard booleano `installed` por um guard baseado na
 * marca, o que resolve dois problemas de uma vez:
 *
 * 1. **Idempotência** (intenção do guard original): remount do RootLayout,
 *    HMR ou StrictMode chamam de novo -> a marca está lá -> no-op, sem
 *    re-wrapear o próprio wrapper e duplicar o processamento.
 * 2. **Resiliência a ser sobrescrito** (Finding 3): o GTM carrega só na
 *    primeira interação do usuário, DEPOIS de nós, e se a inicialização
 *    dele *substituir* `dataLayer.push` em vez de encadear, a nossa
 *    captura seria silenciosamente descartada -- ou seja, praticamente todo
 *    evento real da sessão deixaria de ser capturado. Chamado
 *    periodicamente pelo timer de flush (~10s), isto re-instala sozinho.
 *
 * Devolve `true` se (re)instalou, `false` se já estava no lugar ou se não
 * há `window`. Exportado pra ser testável sem depender do `setInterval`.
 * Nunca lança.
 */
export function ensureNavTrackingHook(): boolean {
  try {
    if (typeof window === "undefined") return false;
    const currentPush = window.dataLayer?.push as unknown as
      | (Record<string, unknown> | undefined);
    if (currentPush && currentPush[NAV_TRACKING_HOOK_MARKER] === true) return false;
    wrapDataLayerPush();
    return true;
  } catch (error) {
    console.error("[navTracking] falha ao instalar hook do dataLayer:", error);
    return false;
  }
}

/**
 * Gate de consentimento é checado a cada push (não só na instalação) --
 * cobre o caso do usuário aceitar o banner NO MEIO da sessão, sem precisar
 * de reload pra a captura começar a valer.
 */
export function installNavTrackingHook(): void {
  ensureNavTrackingHook();
}

const FLUSH_INTERVAL_MS = 10_000;
const EVENTOS_NAVEGACAO_URL = "/tracking/eventos-navegacao";

/**
 * Drena o buffer via sendBeacon. **Nunca lança** -- todo o corpo está dentro
 * de um try/catch (Finding 2 da revisão final). Não é paranoia: é chamado
 * direto de dentro do `useEffect` do RootLayout (src/routes/__root.tsx), e
 * `getOrCreateVisitorId()` pode lançar tanto por `hasCookieConsent()`
 * (getter de `localStorage`) quanto por `crypto.randomUUID()` (ausente em
 * navegadores antigos e em contexto não-seguro). Um throw dentro de um
 * efeito de ROOT não quebra só o tracking -- quebra a hidratação/render do
 * app inteiro. Sendo seguro aqui dentro, nenhum chamador precisa embrulhar.
 *
 * sendBeacon não dá retry nem confirmação de entrega (ver Riscos da spec);
 * se devolver `false` (fila cheia/navegador recusou), o buffer fica intacto
 * e o próximo timer tenta de novo com o buffer maior.
 */
export function flushBuffer(): void {
  try {
    if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") return;
    const events = readBuffer();
    // O endpoint rejeita `eventos: []` com 400 -- nunca manda lote vazio.
    if (events.length === 0) return;

    const visitorId = getOrCreateVisitorId();
    if (!visitorId) {
      // Sem consentimento (ou cookie bloqueado) -- não tem como o CRM
      // atribuir o lote a ninguém. Descarta em vez de acumular pra sempre.
      clearBuffer();
      return;
    }

    const payload = JSON.stringify({ visitor_id: visitorId, eventos: events });
    const sent = navigator.sendBeacon(
      EVENTOS_NAVEGACAO_URL,
      new Blob([payload], { type: "application/json" }),
    );
    if (sent) clearBuffer();
  } catch (error) {
    console.error("[navTracking] falha ao drenar buffer:", error);
  }
}

/**
 * Agenda o flush por timer (~10s) e nos eventos de saída de página
 * (visibilitychange->hidden cobre troca de aba/minimizar; pagehide cobre
 * navegação/fechamento -- mais confiável que `unload` em navegadores
 * modernos). Devolve uma função de cleanup (útil em testes/HMR; RootLayout
 * não desmonta na prática, mas mantém o hook correto).
 *
 * O mesmo timer também re-afirma o hook do `dataLayer`
 * (`ensureNavTrackingHook`) -- ver Finding 3: o GTM carrega depois de nós e
 * pode ter substituído `window.dataLayer.push` nesse meio tempo.
 */
export function scheduleNavTrackingFlush(): () => void {
  const timer = setInterval(() => {
    ensureNavTrackingHook();
    flushBuffer();
  }, FLUSH_INTERVAL_MS);
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") flushBuffer();
  };
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", flushBuffer);

  return () => {
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", flushBuffer);
  };
}
